"use client";

import { useCallback } from "react";

/**
 * Hook for managing PWA app badge (unread count on app icon)
 * Supports Chrome, Edge, Safari (macOS/iOS 16.4+)
 * Falls back silently on unsupported browsers
 */
export function useAppBadge() {
  const setAppBadge = useCallback((count: number) => {
    if (typeof navigator === "undefined") return;

    // Check if Badging API is supported
    if ("setAppBadge" in navigator) {
      try {
        if (count > 0) {
          (navigator as Navigator & { setAppBadge: (count: number) => Promise<void> })
            .setAppBadge(count)
            .catch((error) => {
              // Silently fail - user may not have installed PWA
              console.debug("Failed to set app badge:", error);
            });
        } else {
          (navigator as Navigator & { clearAppBadge: () => Promise<void> })
            .clearAppBadge()
            .catch((error) => {
              console.debug("Failed to clear app badge:", error);
            });
        }
      } catch (error) {
        console.debug("App badge not supported:", error);
      }
    }
  }, []);

  const clearAppBadge = useCallback(() => {
    if (typeof navigator === "undefined") return;

    if ("clearAppBadge" in navigator) {
      try {
        (navigator as Navigator & { clearAppBadge: () => Promise<void> })
          .clearAppBadge()
          .catch((error) => {
            console.debug("Failed to clear app badge:", error);
          });
      } catch (error) {
        console.debug("App badge not supported:", error);
      }
    }
  }, []);

  const isSupported = typeof navigator !== "undefined" && "setAppBadge" in navigator;

  return { setAppBadge, clearAppBadge, isSupported };
}
